import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSermons() {
  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSermon, setEditingSermon] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', vimeo_url: '', speaker: 'Pastor', date: '', is_visible: true
  });

  useEffect(() => { fetchSermons(); }, []);

  const fetchSermons = async () => {
    try {
      const res = await axios.get(`${API}/sermons`, { withCredentials: true });
      setSermons(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSermon) {
        await axios.put(`${API}/sermons/${editingSermon.id}`, formData, { withCredentials: true });
        toast.success('Sermon updated!');
      } else {
        await axios.post(`${API}/sermons`, formData, { withCredentials: true });
        toast.success('Sermon added!');
      }
      setShowForm(false);
      setEditingSermon(null);
      setFormData({ title: '', description: '', vimeo_url: '', speaker: 'Pastor', date: '', is_visible: true });
      fetchSermons();
    } catch (e) { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sermon?')) return;
    try {
      await axios.delete(`${API}/sermons/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchSermons();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const openEdit = (sermon) => {
    setEditingSermon(sermon);
    setFormData(sermon);
    setShowForm(true);
  };

  return (
    <div data-testid="admin-sermons">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Sermons</h1>
          <p className="text-charcoal/60 mt-1">Manage video sermons</p>
        </div>
        <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) setEditingSermon(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-burgundy hover:bg-purple text-white">
              <Plus size={18} className="mr-2" /> Add Sermon
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                {editingSermon ? 'Edit Sermon' : 'Add New Sermon'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div><Label>Title</Label><Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required className="bg-cream" /></div>
              <div><Label>Speaker</Label><Input value={formData.speaker} onChange={(e) => setFormData({...formData, speaker: e.target.value})} className="bg-cream" /></div>
              <div><Label>Vimeo URL</Label><Input value={formData.vimeo_url} onChange={(e) => setFormData({...formData, vimeo_url: e.target.value})} placeholder="https://vimeo.com/..." required className="bg-cream" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required className="bg-cream" /></div>
              <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} className="bg-cream" /></div>
              <Button type="submit" className="w-full bg-burgundy hover:bg-purple">{editingSermon ? 'Update' : 'Add'} Sermon</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : sermons.length > 0 ? (
        <div className="bg-white rounded-2xl border border-gold/10 overflow-hidden">
          <table className="w-full">
            <thead className="bg-cream">
              <tr><th className="text-left p-4 font-medium text-charcoal">Title</th><th className="text-left p-4 font-medium text-charcoal">Speaker</th><th className="text-left p-4 font-medium text-charcoal">Date</th><th className="text-right p-4 font-medium text-charcoal">Actions</th></tr>
            </thead>
            <tbody>
              {sermons.map((s) => (
                <tr key={s.id} className="border-t border-cream">
                  <td className="p-4"><div className="flex items-center gap-2">{s.is_visible === false && <EyeOff size={14} className="text-charcoal/30" />}<span className="text-charcoal">{s.title}</span></div></td>
                  <td className="p-4 text-charcoal/60">{s.speaker}</td>
                  <td className="p-4 text-charcoal/60">{s.date}</td>
                  <td className="p-4 text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(s)}><Edit size={16} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="text-red-500"><Trash2 size={16} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-2xl border border-gold/10">
          <p className="text-charcoal/50">No sermons yet. Add your first one!</p>
        </div>
      )}
    </div>
  );
}
